import { getApiUrl } from "@/utils/api";
import { useState, useEffect } from "react";
import { Sparkles, RefreshCw, Loader2, AlertCircle, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = getApiUrl();

const PERIOD_OPTIONS = [
  { value: "7d", label: "Last 7 Days" },
  { value: "30d", label: "Last 30 Days" },
  { value: "90d", label: "Last 90 Days" },
];

// Parse markdown sections from Claude's response
function parseInsights(text) {
  if (!text) return [];
  const sections = [];
  const parts = text.split(/^##\s+/m).filter(Boolean);
  parts.forEach((part) => {
    const lines = part.split("\n").filter(Boolean);
    const title = lines[0]?.trim();
    const bullets = lines
      .slice(1)
      .filter((l) => l.trim().startsWith("-"))
      .map((l) => l.trim().replace(/^-\s*/, ""));
    if (title) sections.push({ title, bullets });
  });
  return sections;
}

const SECTION_STYLES = {
  "Key Observations": { bg: "bg-blue-50 border-blue-200", title: "text-blue-900", bullet: "bg-blue-500" },
  "Anomalies & Concerns": { bg: "bg-amber-50 border-amber-200", title: "text-amber-900", bullet: "bg-amber-500" },
  "Top Opportunities": { bg: "bg-emerald-50 border-emerald-200", title: "text-emerald-900", bullet: "bg-emerald-500" },
  "Quick Wins (Do This Week)": { bg: "bg-purple-50 border-purple-200", title: "text-purple-900", bullet: "bg-purple-500" },
};

function InsightSection({ section }) {
  const style = SECTION_STYLES[section.title] || {
    bg: "bg-gray-50 border-gray-200",
    title: "text-gray-900",
    bullet: "bg-gray-400",
  };
  return (
    <div className={`border rounded-lg p-5 ${style.bg}`}>
      <h4 className={`text-sm font-semibold mb-3 ${style.title}`}>{section.title}</h4>
      {section.bullets.length > 0 ? (
        <ul className="space-y-2">
          {section.bullets.map((bullet, i) => (
            <li key={i} className="flex gap-2.5 text-sm text-gray-700">
              <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${style.bullet}`} />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-600">{section.title}</p>
      )}
    </div>
  );
}

export default function AnalyticsAI({ period }) {
  const [insights, setInsights] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [insightPeriod, setInsightPeriod] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(period || "30d");

  // Load cached insights on mount
  useEffect(() => {
    axios
      .get(`${API}/analytics/ai-insights/cached`, { withCredentials: true })
      .then((r) => {
        if (r.data.insights) {
          setInsights(r.data.insights);
          setGeneratedAt(r.data.generated_at);
          setInsightPeriod(r.data.period);
        }
      })
      .catch(() => {});
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/analytics/ai-insights?period=${selectedPeriod}`,
        {},
        { withCredentials: true }
      );
      setInsights(r.data.insights);
      setGeneratedAt(r.data.generated_at);
      setInsightPeriod(r.data.period);
      toast.success("AI insights generated successfully");
    } catch (e) {
      toast.error("Failed to generate insights — check API key balance");
    } finally {
      setGenerating(false);
    }
  };

  const sections = parseInsights(insights);
  const formattedDate = generatedAt
    ? new Date(generatedAt).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div className="space-y-5" data-testid="analytics-ai-insights">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-lg p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-5 w-5 text-primary" />
              <h3 className="font-serif text-base font-medium">AI-Powered Insights</h3>
            </div>
            <p className="text-xs text-muted-foreground max-w-lg">
              Claude Sonnet 4.5 analyses your analytics data and highlights what's happening, why it matters,
              and exactly what to do next to grow conversions.
            </p>
          </div>
          {insights && (
            <div className="shrink-0 text-right">
              <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                <Calendar className="h-3 w-3" />
                {formattedDate}
              </p>
              {insightPeriod && (
                <p className="text-xs text-muted-foreground">
                  {PERIOD_OPTIONS.find((p) => p.value === insightPeriod)?.label}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-4">
          <div className="flex gap-1">
            {PERIOD_OPTIONS.map((p) => (
              <button
                key={p.value}
                onClick={() => setSelectedPeriod(p.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  selectedPeriod === p.value
                    ? "bg-primary text-white"
                    : "bg-white/70 text-muted-foreground hover:bg-white"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <Button
            onClick={generate}
            disabled={generating}
            size="sm"
            className="rounded-none text-xs ml-auto"
            data-testid="generate-ai-insights-btn"
          >
            {generating ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Analysing…</>
            ) : (
              <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> {insights ? "Re-analyse" : "Analyse Now"}</>
            )}
          </Button>
        </div>
      </div>

      {/* Insights display */}
      {generating && (
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <div className="relative">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <Sparkles className="h-4 w-4 text-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium">Claude is analysing your data…</p>
            <p className="text-xs text-muted-foreground mt-0.5">This usually takes 10–20 seconds</p>
          </div>
        </div>
      )}

      {!generating && insights && sections.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sections.map((section, i) => (
            <InsightSection key={i} section={section} />
          ))}
        </div>
      )}

      {!generating && insights && sections.length === 0 && (
        <div className="bg-white border border-border rounded-lg p-5">
          <pre className="text-sm whitespace-pre-wrap text-gray-700 font-sans leading-relaxed">
            {insights}
          </pre>
        </div>
      )}

      {!generating && !insights && (
        <div className="bg-white border border-border rounded-lg p-12 text-center">
          <Sparkles className="h-10 w-10 mx-auto text-muted-foreground/40 mb-4" />
          <p className="text-sm font-medium mb-1">No insights generated yet</p>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto">
            Click "Analyse Now" to have Claude Sonnet 4.5 review your analytics and surface
            actionable insights for your real estate business.
          </p>
        </div>
      )}

      {!generating && !insights && (
        <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 flex gap-3 text-xs text-amber-800">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <strong>Note:</strong> AI insights use your Anthropic API key (with Emergent fallback).
            Make sure you have sufficient balance. Insights are cached — re-analyse any time to refresh.
          </div>
        </div>
      )}
    </div>
  );
}
